/**
 * Token storage abstraction layer.
 *
 * Currently uses sessionStorage (scoped to tab, cleared on close).
 * This is safer than localStorage because:
 * - Token is not shared across tabs (limits blast radius of XSS)
 * - Token is cleared when the browser tab/window closes
 *
 * Future: migrate to httpOnly cookie set by the backend /Token endpoint.
 * That would make the token completely invisible to JavaScript.
 */

const TOKEN_KEY = 'unilinkApiToken';
const USER_KEY = 'unilinkApiUser';

export const getToken = () => {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
};

export const getUser = () => {
  return sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);
};

export const setToken = (token) => {
  sessionStorage.setItem(TOKEN_KEY, token);
  // Keep localStorage as fallback during migration — remove once stable
  localStorage.setItem(TOKEN_KEY, token);
};

export const setUser = (user) => {
  sessionStorage.setItem(USER_KEY, user);
  localStorage.setItem(USER_KEY, user);
};

export const clearTokens = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Trusted-device token for MFA ("skip the code on this browser for 90 days").
 * Deliberately in localStorage and NOT removed by clearTokens(): it must survive logout.
 * Storage can throw (private mode, disabled storage) — MFA login must still work then.
 */
const TRUSTED_DEVICE_KEY = 'unilinkTrustedDevice';

export const getTrustedDeviceToken = () => {
  try {
    return localStorage.getItem(TRUSTED_DEVICE_KEY);
  } catch {
    return null;
  }
};

export const setTrustedDeviceToken = (token) => {
  try {
    localStorage.setItem(TRUSTED_DEVICE_KEY, token);
  } catch {
    /* storage unavailable — the user will be asked for a code next time */
  }
};

export const clearTrustedDeviceToken = () => {
  try {
    localStorage.removeItem(TRUSTED_DEVICE_KEY);
  } catch {
    /* storage unavailable */
  }
};
